<script lang="ts">
  import { Button } from '@aatmanova/ui';
  import type { AatmanovaClient } from '@aatmanova/sdk';

  interface Props {
    client: AatmanovaClient;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
    invalidateProducts: () => void;
  }

  let { client, addToast, invalidateProducts }: Props = $props();

  let isSyncing = $state(false);
  let isReindexing = $state(false);
  let isResetting = $state(false);
  let showResetConfirm = $state(false);
  let adminPassword = $state('');
  let maintenanceLogs = $state<string[]>(['System ready. Standing by.']);

  function addLog(msg: string) {
    const time = new Date().toLocaleTimeString();
    maintenanceLogs = [...maintenanceLogs, `[${time}] ${msg}`];
  }

  async function triggerSync() {
    try {
      isSyncing = true;
      addLog('INITIATING CATALOG SYNCHRONIZATION...');
      const res = await client.syncProducts();
      addLog(`SUCCESS: ${res.message}`);
      addToast('Catalog synchronized successfully', 'success');
      invalidateProducts();
    } catch (err: any) {
      addLog(`ERROR: ${err.message}`);
      addToast(err.message || 'Sync failed', 'error');
    } finally {
      isSyncing = false;
    }
  }

  async function triggerReindex() {
    try {
      isReindexing = true;
      addLog('INITIATING SEARCH INDEX REBUILD...');
      const res = await client.reindexProducts();
      addLog(`SUCCESS: ${res.message}`);
      addToast('Search indexing complete', 'success');
    } catch (err: any) {
      addLog(`ERROR: ${err.message}`);
      addToast(err.message || 'Reindexing failed', 'error');
    } finally {
      isReindexing = false;
    }
  }

  async function triggerReset() {
    if (!adminPassword) {
      addToast('Please enter your admin password.', 'warning');
      return;
    }
    try {
      isResetting = true;
      addLog('CRITICAL: RESETTING ENTIRE INVENTORY AND EXPORTING BACKUP...');
      const blob = await client.resetInventory(adminPassword);
      addLog('DATABASE RESET SUCCESSFUL. INITIATING AUTO-DOWNLOAD OF CATALOG BACKUP...');
      
      // Auto download the backup ZIP
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      addLog('SUCCESS: Inventory reset and backup download completed.');
      addToast('All inventory deleted and backup exported successfully', 'success');
      showResetConfirm = false;
      adminPassword = '';
      invalidateProducts();
    } catch (err: any) {
      addLog(`ERROR: ${err.message}`);
      addToast(err.message || 'Reset failed', 'error');
    } finally {
      isResetting = false;
    }
  }
</script>

<div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
  <!-- Action Cards -->
  <div class="lg:col-span-2 space-y-6">
    <!-- Rebuild Search Index -->
    <div class="p-6 bg-base-100 border border-base-300 rounded-2xl shadow-xl flex items-start gap-4 hover:border-primary/20 transition-all duration-300">
      <div class="p-3 bg-primary/10 text-primary rounded-2xl">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
      </div>
      <div class="space-y-3 flex-1">
        <div class="space-y-1">
          <h4 class="font-bold text-base-content">Rebuild Search Index</h4>
          <p class="text-xs text-base-content/60">Rebuild the database search index. Ensures search filters, autocomplete, and product queries stay accurate and up to date.</p>
        </div>
        <Button onclick={triggerReindex} disabled={isReindexing} variant="primary" size="sm" class="font-bold shadow-md">
          {isReindexing ? 'Indexing...' : 'Initiate Reindex'}
        </Button>
      </div>
    </div>

    <!-- Sync Catalog -->
    <div class="p-6 bg-base-100 border border-base-300 rounded-2xl shadow-xl flex items-start gap-4 hover:border-secondary/20 transition-all duration-300">
      <div class="p-3 bg-secondary/10 text-secondary rounded-2xl">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89H17v4.67"/></svg>
      </div>
      <div class="space-y-3 flex-1">
        <div class="space-y-1">
          <h4 class="font-bold text-base-content">Sync Platform Catalog</h4>
          <p class="text-xs text-base-content/60">Sync the local database with central platform servers to fetch the latest pricing updates and stock configurations.</p>
        </div>
        <Button onclick={triggerSync} disabled={isSyncing} variant="secondary" size="sm" class="font-bold">
          {isSyncing ? 'Syncing...' : 'Initiate Sync'}
        </Button>
      </div>
    </div>

    <!-- Factory Reset -->
    <div class="p-6 bg-base-100 border border-error/20 rounded-2xl shadow-xl flex items-start gap-4 hover:border-error/40 transition-all duration-300">
      <div class="p-3 bg-error/10 text-error rounded-2xl">
        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
      </div>
      <div class="space-y-3 flex-1">
        <div class="space-y-1">
          <h4 class="font-bold text-error">Reset Catalog Database</h4>
          <p class="text-xs text-base-content/60">Permanently delete all categories, subcategories, products, and associated media files. This action cannot be undone.</p>
        </div>
        {#if !showResetConfirm}
          <Button onclick={() => { showResetConfirm = true; adminPassword = ''; }} variant="outline" size="sm" class="text-error border-error/30 hover:bg-error hover:text-text-inverse font-bold">
            Wipe Catalog
          </Button>
        {:else}
          <div class="p-4 border border-error rounded-xl bg-error/5 space-y-3">
            <p class="text-xs font-bold text-error">🚨 WARNING: This will permanently delete all products, categories, and images. A safety backup ZIP will be generated and downloaded automatically.</p>
            <div class="form-control">
              <span class="text-xs font-bold text-base-content/60 mb-2 uppercase tracking-wider">Confirm Admin Password</span>
              <input type="password" bind:value={adminPassword} placeholder="Enter your admin password" class="input input-sm input-bordered bg-base-200 text-base-content w-full" required />
            </div>
            <div class="flex items-center gap-2">
              <Button onclick={triggerReset} disabled={isResetting || !adminPassword} size="xs" variant="error" class="border-0 font-bold">
                {isResetting ? 'Processing...' : 'YES, PERMANENTLY ERASE'}
              </Button>
              <button onclick={() => { showResetConfirm = false; adminPassword = ''; }} class="btn btn-xs btn-outline font-bold">Cancel</button>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Console Monitor -->
  <div class="bg-black text-green-400 p-5 rounded-2xl border border-base-300 font-mono text-xs flex flex-col h-[400px] shadow-2xl">
    <div class="flex items-center justify-between pb-3 border-b border-green-500/20 mb-3 text-[10px] text-green-400/60 uppercase tracking-widest font-bold">
      <span>Active Operations Monitor</span>
      <span class="flex h-2 w-2 relative">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
      </span>
    </div>
    <div class="flex-1 overflow-y-auto space-y-1.5 scrollbar-thin select-text">
      {#each maintenanceLogs as log}
        <div class="leading-relaxed whitespace-pre-wrap">{log}</div>
      {/each}
    </div>
    <div class="pt-3 border-t border-green-500/20 mt-3 text-right">
      <button onclick={() => maintenanceLogs = ['[Console Monitor Reset]']} class="text-[10px] text-green-400/50 hover:text-green-400 font-bold uppercase tracking-wider">Clear Logs</button>
    </div>
  </div>
</div>

<style>
  .scrollbar-thin::-webkit-scrollbar { width: 4px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
  .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(74, 222, 128, 0.2); border-radius: 2px; }
</style>
